import { createSlice } from '@reduxjs/toolkit';
import { Action } from 'redux';
import { getUserInitials } from 'utils/stringUtils';
import { DictionaryDto } from 'api/dictionary';
import { fetchAccessedStatuses, StatusCommonDto } from 'api/statuses';
import { AppThunk } from '../store';
import { fetchCurrentUser, UserDto } from '../../api/user';
import { getJwtToken, setJwtToken } from '../../utils/jwt';
import { fetchRoles } from '../../api/role';
import { setMenuItems } from './menuSlice';

export interface UserInfoState {
    userName: string;
    fullName: string;
    email: string;
    permissions: string[];
    roles: string[];
    token?: string;
    region?: DictionaryDto | undefined;
    branch?: DictionaryDto | null | undefined;
    loading: boolean;
    userData?: UserDto;
    roleLoading?: boolean;
    selectRoleOpen?: boolean;
    roleList?: DictionaryDto[];
}

export const initialState: UserInfoState = {
    userName: '',
    email: '',
    permissions: [],
    roles: [],
    token: '',
    fullName: '',
    loading: true,
};

const userInfoSlice = createSlice({
    name: 'userInfo',
    initialState,
    reducers: {
        setUserInfo(state, action: Action & { payload: UserInfoState | undefined }) {
            return { ...state, ...(action.payload || state.userData) };
        },
        setToken(state, action: Action & { payload: string }) {
            setJwtToken(action.payload);
            state.token = action.payload;
            return state;
        },
        logout() {
            setJwtToken('');
            localStorage.removeItem('permissions');
            return initialState;
        },
        initState(): any {
            return initialState;
        },
        setLoading(state, action: Action & { payload: boolean }) {
            state.loading = action.payload;
            return state;
        },
        setPermissions(state, action: Action & { payload: string[] }) {
            localStorage.setItem('permissions', JSON.stringify(action.payload));
            state.permissions = action.payload;
            return state;
        },
        setUserData(state, action: Action & { payload: UserDto }) {
            state.userData = action.payload;
            return state;
        },
        setRoleLoading(state, action: Action & { payload: boolean }) {
            state.roleLoading = action.payload;
            return state;
        },
        setRoleOpen(state, action: Action & { payload: boolean }) {
            state.selectRoleOpen = action.payload;
            return state;
        },
        setRoleList(state, action: Action & { payload: DictionaryDto[] }) {
            state.roleList = action.payload;
            return state;
        },
    },
});

export const {
    setUserInfo,
    setToken,
    logout,
    initState,
    setLoading,
    setPermissions,
    setUserData,
    setRoleLoading,
    setRoleOpen,
    setRoleList,
} = userInfoSlice.actions;

export default userInfoSlice.reducer;

export const { getUserInfo, getPermissions, getActiveRole } = {
    getUserInfo(state: { userInfo: UserInfoState }) {
        return state.userInfo;
    },

    getPermissions(state: { userInfo: UserInfoState }) {
        return state.userInfo.permissions;
    },
    getActiveRole(state: { userInfo: UserInfoState }) {
        if (state.userInfo.roles.length > 1) {
            const [permission] = state.userInfo.permissions;
            return state.userInfo.roleList?.find((r) => r.code === permission)?.description;
        }

        return undefined;
    },
};

export const loadInitialUserData = (): AppThunk => async (dispatch) => {
    try {
        const token = getJwtToken() || '';

        if (token) {
            try {
                const data = await fetchCurrentUser();
                const statuses = await fetchAccessedStatuses();
                const permString = localStorage.getItem('permissions');
                const permissions = permString ? JSON.parse(permString) : [];
                const userMapped = mapUser(data);
                userMapped.permissions = data.roles?.length > 1 ? permissions : data.roles;
                if (data.roles.length > 1) {
                    const roleList = await fetchRoles();
                    dispatch(setRoleList(roleList.content));
                    dispatch(setRoleLoading(false));
                }
                dispatch(setUserInfo(userMapped));
                dispatch(setPermissions(userMapped.permissions));
                dispatch(setMenuItems(mapStatuses(statuses)));
            } catch (err) {
                console.log(err);
            }
        }

        dispatch(setLoading(false));
    } catch (e) {
        dispatch(setLoading(false));
    }
};

export const loadUserAsync = (redirect: () => void): AppThunk => async (dispatch) => {
    try {
        const data = await fetchCurrentUser();
        if (data.roles.length > 1) {
            dispatch(setRoleOpen(true));
            dispatch(setRoleLoading(true));
            dispatch(setUserData(data));
            const roleList = await fetchRoles();
            dispatch(setRoleList(roleList.content));
            dispatch(setRoleLoading(false));
        } else {
            dispatch(setUserInfo(mapUser(data)));
            redirect();
        }
    } catch (e) {
        console.log(e);
    }
};

export const closeRoleSelectAsync = (
    selected: boolean,
    role: string,
    redirect: () => void,
    isLogin: boolean
): AppThunk => async (dispatch) => {
    if (selected) {
        try {
            dispatch(setRoleLoading(true));
            redirect();
            dispatch(setUserInfo(undefined));
            dispatch(setPermissions([role]));
            const statuses = await fetchAccessedStatuses();
            dispatch(setMenuItems(mapStatuses(statuses)));
            dispatch(setRoleOpen(false));
            setTimeout(() => {
                dispatch(setRoleLoading(false));
            });
        } catch (e) {
            console.log(e);
            dispatch(setRoleLoading(false));
        }
    } else {
        if (isLogin) {
            dispatch(logout());
        }
    }

    dispatch(setRoleOpen(false));
};

const mapUser = (data: UserDto): UserInfoState => {
    return {
        userName: data.username,
        fullName: getUserInitials(data.last_name, data.first_name, ''),
        email: data.email,
        region: data.region as DictionaryDto,
        branch: {
            relog_api_key: data.branch?.relog_api_key,
            relog_enabled: data.branch?.relog_enabled,
        } as Object as DictionaryDto,
        permissions: data.roles?.length > 1 ? [] : data.roles,
        roles: data.roles,
        loading: false,
    };
};

const mapStatuses = (statuses: StatusCommonDto[]) => {
    return {
        name: 'OrdersByStatuses',
        items: statuses.map((i) => ({
            id: Number(i.id),
            name: i.description,
            path: `/cabinet/requests/${i.id}`,
            doubleSelect: i.is_mass_sampling,
            code: i.code,
        })),
    };
};
